import React, {
	useEffect,
	useReducer,
	useRef,
	useContext,
} from "react";

import {
	Avatar,
	Box,
	Container,
	Typography,
	makeStyles,
} from "@material-ui/core";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(() => ({
	messagesContainer: {
		display: "flex",
		flexDirection: "column",
		width: "100%",
		alignItems: "center",
		gap: "40vh",
		overflowY: "scroll",
		padding: "43vh 0 45vh 0",
	},
	messageCard: {
		display: "flex",
		color: "rgba(0, 0, 0, 0.70)",
		backgroundColor: "white",
		padding: "20px 10px",
		borderRadius: "10px 10px 10px 10px",
		width: "640px",
		boxShadow: "0 3px 5px 5px rgba(31, 38, 135, 0.37)",
		gap: "20px",
	},
	avatar: {
		marginLeft: "-30px",
		boxShadow: "0 0px 5px 5px rgba(31, 38, 135, 0.37)",
	},
	cardText: {
		display: "flex",
		flexDirection: "column",
		gap: "5px",
	},
	name: {
		fontSize: "0.8rem",
		fontWeight: "bold",
	},
	message: {},
}));

const reducer = (state, action) => {
	if (action.type === "LOAD_MESSAGES") {
		const messages = action.payload;
		const newMessages = [];

		messages.forEach((message) => {
			const messageIndex = state.findIndex((m) => m.id === message.id);
			if (messageIndex !== -1) {
				state[messageIndex] = message;
			} else {
				newMessages.push(message);
			}
		});

		return [...newMessages, ...state];
	}

	if (action.type === "ADD_MESSAGE") {
		const newMessage = action.payload;
		const messageIndex = state.findIndex((m) => m.id === newMessage.id);

		if (messageIndex !== -1) {
			state[messageIndex] = newMessage;
		} else {
			state.push(newMessage);
		}

		return [...state];
	}

	if (action.type === "UPDATE_MESSAGE") {
		const messageToUpdate = action.payload;
		const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

		if (messageIndex !== -1) {
			state[messageIndex] = messageToUpdate;
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

function PresentationMessagesList({
	ticketId,
	activeIndex,
	setMessagesListLength,
}) {
	const classes = useStyles();
	const [messagesList, dispatch] = useReducer(reducer, []);
	const lastMessageRef = useRef(null);
	const currentTicketId = useRef(ticketId);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		dispatch({ type: "RESET" });
		currentTicketId.current = ticketId;
	}, [ticketId]);

	useEffect(() => {
		setMessagesListLength(messagesList.length);
	}, [messagesList, setMessagesListLength]);

	useEffect(() => {
		const socket = openSocket({
			scope: "presentationmessages",
			userId: user.id,
			component: "PresentationMessagesList",
		});

		socket.on("connect", () => socket.emit("joinChatBox", ticketId));

		socket.on("appMessage", (data) => {
			if (data.action === "create") {
				dispatch({ type: "ADD_MESSAGE", payload: data.message });
				if (lastMessageRef.current) {
					lastMessageRef.current.scrollIntoView({});
				}
			}

			if (data.action === "update") {
				dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [ticketId, user.id]);

	useEffect(() => {
		const timer = setTimeout(() => {
			const section = document.querySelector("#searchedClicked");
			if (section) {
				section.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [activeIndex]);

	return (
		<Container className={classes.messagesContainer}>
			{messagesList.map((message, index) => (
				<Box
					className={classes.messageCard}
					key={message.id || index}
					id={index === activeIndex ? "searchedClicked" : undefined}
					ref={index === messagesList.length - 1 ? lastMessageRef : null}
				>
					<Avatar
						className={classes.avatar}
						src={message.ticket.contact.profilePicUrl}
					/>
					<Box className={classes.cardText}>
						<Typography className={classes.name}>
							{message.ticket.contact.name}
						</Typography>
						<Typography className={classes.message}>
							{message.body}
						</Typography>
					</Box>
				</Box>
			))}
		</Container>
	);
}

export default PresentationMessagesList;
